const DAY_MS = 24 * 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;

function startOfDay(date) {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function endOfDay(date) {
  const normalized = new Date(date);
  normalized.setHours(23, 59, 59, 999);
  return normalized;
}

function addMinutes(date, minutes) {
  return new Date(date.getTime() + minutes * MINUTE_MS);
}

function dateWithTime(dateString, timeString = '23:59') {
  const [hours = 23, minutes = 59] = timeString.split(':').map(Number);
  const date = new Date(dateString);
  date.setHours(hours, minutes, 0, 0);
  return date;
}

function getSimpleTaskDueDate(task) {
  if (!task?.dueDate) return null;

  if (task.startTime && task.duration) {
    return addMinutes(dateWithTime(task.dueDate, task.startTime), Number(task.duration));
  }

  return endOfDay(task.dueDate);
}

function getTaskStartDate(task, dueDate) {
  if (task.createdAt) {
    const createdAt = new Date(task.createdAt);
    if (!Number.isNaN(createdAt.getTime()) && createdAt < dueDate) {
      return createdAt;
    }
  }

  const fallback = new Date(dueDate);
  fallback.setDate(fallback.getDate() - 1);
  return fallback;
}

function getWeightedWorkPercent(task) {
  const subtasks = task.subTasks || [];
  const completedSubtasks = subtasks.filter(subtask => subtask.completed).length;
  const totalSubtasks = subtasks.length;
  const durationWeight = subtask => Math.max(1, Number(subtask.duration) || 30);
  const totalDuration = subtasks.reduce((sum, subtask) => sum + durationWeight(subtask), 0);
  const completedDuration = subtasks
    .filter(subtask => subtask.completed)
    .reduce((sum, subtask) => sum + durationWeight(subtask), 0);
  const workPercent = totalDuration > 0
    ? Math.round((completedDuration / totalDuration) * 100)
    : task.completed ? 100 : 0;

  return {
    completedSubtasks,
    totalSubtasks,
    completedDuration,
    totalDuration,
    workPercent
  };
}

function getTimePressurePercent(start, due, reference) {
  const totalWindow = Math.max(MINUTE_MS, due - start);
  const elapsed = reference - start;
  return Math.round((elapsed / totalWindow) * 100);
}

function getTone(task, tuScore) {
  if (!task.completed && tuScore < -20) return 'red';
  if (!task.completed && tuScore < -5) return 'yellow';
  return 'green';
}

function getTimeUnitStatus(tuScore) {
  if (tuScore > 5) return 'ahead';
  if (tuScore < -5) return 'behind';
  return 'onTrack';
}

function getPriorityMultiplier(task) {
  if (task?.priority === 'high') return 1.5;
  if (task?.priority === 'low') return 0.7;
  return 1;
}

function getWeightedTimeUnitScore(task, workPercent, timePressurePercent) {
  const priorityMultiplier = getPriorityMultiplier(task);
  const rawScore = (workPercent - timePressurePercent) * priorityMultiplier;

  return {
    priorityMultiplier,
    tuScore: clamp(Math.round(rawScore), -150, 150)
  };
}

export function getTaskTimeUnits(task, now = new Date()) {
  if (!task?.dueDate) return null;

  const reference = task.completed && task.completedAt ? new Date(task.completedAt) : now;
  const safeReference = Number.isNaN(reference.getTime()) ? now : reference;

  if (task.isMultiDay && task.endDate) {
    const start = startOfDay(task.dueDate);
    const due = endOfDay(task.endDate);
    const totalDays = Math.max(1, Math.round((startOfDay(task.endDate) - start) / DAY_MS) + 1);
    const elapsedDays = clamp(Math.round((startOfDay(safeReference) - start) / DAY_MS) + 1, 0, totalDays);
    const timePressurePercent = getTimePressurePercent(start, due, safeReference);
    const timePercent = clamp(Math.round((elapsedDays / totalDays) * 100), 0, 100);
    const weighted = getWeightedWorkPercent(task);
    const { priorityMultiplier, tuScore } = getWeightedTimeUnitScore(task, weighted.workPercent, timePressurePercent);

    return {
      totalDays,
      elapsedDays,
      dayLabel: elapsedDays === 0 ? 0 : elapsedDays,
      timePercent,
      timePressurePercent,
      priorityMultiplier,
      ...weighted,
      subtaskPercent: weighted.workPercent,
      tuScore,
      tuStatus: getTimeUnitStatus(tuScore),
      tone: getTone(task, tuScore),
      applies: true
    };
  }

  const due = getSimpleTaskDueDate(task);
  if (!due) return null;

  const start = getTaskStartDate(task, due);
  const timePressurePercent = getTimePressurePercent(start, due, safeReference);
  const weighted = getWeightedWorkPercent(task);
  const { priorityMultiplier, tuScore } = getWeightedTimeUnitScore(task, weighted.workPercent, timePressurePercent);

  return {
    totalDays: 1,
    elapsedDays: safeReference <= due ? 0 : 1,
    dayLabel: safeReference <= due ? 0 : 1,
    timePercent: clamp(timePressurePercent, 0, 100),
    timePressurePercent,
    priorityMultiplier,
    ...weighted,
    subtaskPercent: weighted.workPercent,
    tuScore,
    tuStatus: getTimeUnitStatus(tuScore),
    tone: getTone(task, tuScore),
    applies: true
  };
}

export function getMergedTimeUnitPoints(task, now = new Date()) {
  const timeUnits = getTaskTimeUnits(task, now);
  if (!timeUnits) {
    return {
      timeUnits: 0,
      pointsAdjustment: 0
    };
  }

  return {
    timeUnits: timeUnits.tuScore,
    pointsAdjustment: 0
  };
}

export function getMultiDayProgress(task, now = new Date()) {
  if (!task?.isMultiDay || !task.dueDate || !task.endDate) {
    return null;
  }

  return getTaskTimeUnits(task, now);
}

export function formatDurationLabel(minutes) {
  if (!minutes) return '';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours > 0 ? `${hours}h ` : ''}${mins > 0 ? `${mins}m` : ''}`.trim();
}
